import { Modal } from "react-bootstrap";
import useStore from "../hooks/useStore";
import useFlow from "../hooks/useFlow";
import { clearHubSpotState } from "../utils/hubspot";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PhoneInputWithNumpad from "./PhoneInputWithNumpad";
import { itemDisplayName } from "../utils/storeItem";

export default function FindClientModal() {
  const {
    showFindClientModal,
    setShowFindClientModal,
    setSelectedClient,
    selectedPopularItem,
    setShowCheckoutModal,
  } = useStore();

  const { findClientByPhone, openRegisterModal, locationPreferences } =
    useFlow();

  const [phone, setPhone] = useState("");
  const [clients, setClients] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const phoneInputRef = useRef(null);

  useEffect(() => {
    if (showFindClientModal) phoneInputRef.current.focus();
  }, [showFindClientModal]);

  const findClients = async (phoneOverride) => {
    try {
      const rawPhone = phoneOverride ?? phone;
      const unmaskedPhone = rawPhone.replace(/[^\d]/g, "");

      // Validate phone number
      const phoneRegex = /^(\+1|1)?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
      if (!phoneRegex.test(unmaskedPhone)) {
        toast.error("Please enter a valid American phone number.");
        return;
      }

      setSearched(false);
      setLoading(true);
      setClients([]);
      const data = await findClientByPhone(unmaskedPhone);
      setClients(data);
      setLoading(false);
      setSearched(true);
    } catch (e) {
      setLoading(false);
      const msg = e?.message?.trim();
      const isNoResults =
        !msg ||
        msg.includes("not defined") ||
        msg === "[object Object]";
      toast.error(
        isNoResults
          ? "No results found for this phone number. Please check the number and try again."
          : `Something went wrong! ${msg}`
      );
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setShowFindClientModal(false);
    setShowCheckoutModal(true);
  };

  const handleCreateAccount = () => {
    setShowFindClientModal(false);
    openRegisterModal();
  };

  const handleClose = () => {
    setShowFindClientModal(false);
    clearHubSpotState();
  };

  return (
    <Modal
      show={showFindClientModal}
      onHide={handleClose}
      centered
      size="lg"
    >
      <Modal.Body className="px-5 text-center">
        <button
          type="button"
          className="btn-close"
          data-testid="find-client-modal-close-btn"
          onClick={handleClose}
        ></button>
        <div className="secondary-screen px-5">
          <div className="row text-center pt-5 pt-md-0 pb-5">
            <div className="col-md-8 m-auto pt-5 pt-md-0 ">
              <h3 className="py-2 fw-light">
                Let&apos;s find your account <br />
                <b className="fw-bold">enter your phone number</b>
              </h3>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-4 p-0 mb-5">
              <PhoneInputWithNumpad
                value={phone}
                onChange={setPhone}
                placeholder="(512) 555-0123"
                disabled={loading}
                showNumpad={locationPreferences?.showPhoneNumpad}
                numpadInline
                inputRef={phoneInputRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") findClients();
                }}
                onComplete={(val) =>
                  findClients((val || "").replace(/\D/g, ""))
                }
              />
            </div>
            <div className="col-auto">
              <a
                className="btn btn-lg btn-primary"
                data-testid="find-client-search-btn"
                onClick={() => findClients(phone)}
              >
                {loading ? (
                  <span
                    className="spinner-grow spinner-grow-sm"
                    aria-hidden="true"
                  />
                ) : (
                  "Search"
                )}
              </a>
            </div>

            {selectedPopularItem && searched && (
              <div className="col-md-12" id="event_details_message">
                <p>
                  Please select your account to purchase{" "}
                  <b>{itemDisplayName(selectedPopularItem)}</b>
                </p>
              </div>
            )}
            {searched && (
              <div className="py-3 mt-5 border d-block modalSearchedList">
                {clients.length === 0 && (
                  <div className="text-center py-5">
                    <h3 className="fw-light">No results found.</h3>
                    <p className="fw-light">
                      We couldn&apos;t find an account with that phone number.
                    </p>
                  </div>
                )}

                {clients.map((client, idx) => (
                  <div
                    className="row py-2 d-flex student justify-content-center"
                    role="button"
                    key={idx}
                  >
                    <div className="col-8 p-4 bg-white shadow-sm rounded-4 border">
                      <p className="float-start p-0 m-0 student-name">
                        {client.name}
                      </p>
                      <button
                        type="button"
                        className="btn btn-lg btn-outline-primary float-end select-account-for-event checkout-btn"
                        data-testid="find-client-select-btn"
                        onClick={() => handleSelectClient(client)}
                      >
                        Select
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="w-100 text-center mt-4 pb-5">
              <a
                className="sign-up-modal btn btn-outline-danger btn-lg"
                data-testid="find-client-signup-btn"
                onClick={handleCreateAccount}
              >
                Create an account
              </a>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
